import { computed, toValue } from 'vue'
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { availableLanguages } from '@/i18n'
import { localizedOgLocale } from '@/utils/seoLocale'
import { resolveRouteRobots } from '@/utils/seoRobots'

/**
 * Routes that own their full SEO head via useHead. App-level useDefaultHead()
 * must not set homepage defaults here.
 *
 * Home / Recipes / RecipeDetails are also edge-injected by the Cloudflare Worker.
 * Client head on those routes must mirror backend PageSeo/RecipeSeo and must
 * never downgrade edge tags on an incomplete fetch (see SEO_DEINDEXING_2026.md).
 */
const USE_HEAD_OWNED_ROUTES = new Set([
  'Home',
  'Recipes',
  'RecipeDetails',
  'content-page',
  'Login',
  'Register',
  'ForgotPassword',
  'ResetPassword',
  'VerifyEmail',
  'Landing',
  'Settings',
  'FamilyGroup',
  'JoinGroup',
  'ManageNotifications',
  'Upgrade',
])

/**
 * Routes that call useDefaultHead({ ...custom keys }) themselves. The App-level
 * empty call skips them; the page-level call still applies.
 */
const CUSTOM_DEFAULT_HEAD_ROUTES = new Set([
  'FreeMealPlan',
  'PlannerLanding',
  'GroceriesLanding',
])

const IOS_PWA_META = [
  { name: 'theme-color', content: '#ffffff' },
  { name: 'apple-mobile-web-app-capable', content: 'yes' },
  { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' }
]

function minimalHead (t) {
  return {
    meta: [
      { charset: 'UTF-8' },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      },
      ...IOS_PWA_META,
      { name: 'google-adsense-account', content: t('head.google_adsense_account') }
    ],
    link: [{ rel: 'icon', href: '/yumnia-Favicon.ico' }]
  }
}

export function useDefaultHead (options = {}) {
  const {
    titleKey = 'head.title',
    titleFallback = '',
    descriptionKey = 'head.description',
    descriptionFallback = '',
    keywordsKey = '',
    keywordsFallback = '',
    baseUrl = import.meta.env.VITE_SITE_URL || 'https://yumnia.com'
  } = options

  const route = useRoute()
  const { t, locale } = useI18n()
  // App.vue calls useDefaultHead() with no / empty options. Page owners pass keys.
  const isPageOwnedCall = Object.keys(options).length > 0

  useHead(computed(() => {
    if (!route) {
      return {}
    }

    if (USE_HEAD_OWNED_ROUTES.has(route.name)) {
      return minimalHead(t)
    }

    if (!isPageOwnedCall && CUSTOM_DEFAULT_HEAD_ROUTES.has(route.name)) {
      return minimalHead(t)
    }

    // Keys may be strings or refs/computed (e.g. premium vs free SEO keys)
    const titleK = titleKey != null ? toValue(titleKey) : ''
    const descK = descriptionKey != null ? toValue(descriptionKey) : ''
    const kwK = keywordsKey != null ? toValue(keywordsKey) : ''

    const pageTitle = titleK ? t(titleK) || titleFallback : t('head.title')
    const pageDescription = descK ? t(descK) || descriptionFallback : t('head.description')
    const pageKeywords = kwK ? t(kwK) || keywordsFallback : ''
    // Keep SEO canonical stable on legacy aliases.
    const canonicalPath = route.name === 'FreeMealPlan' ? '/ai-diet-planner' : route.path
    const defaultHead = {
      title: pageTitle,
      meta: [
        { charset: 'UTF-8' },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        },
        ...IOS_PWA_META,
        { key: 'description', name: 'description', content: pageDescription },
        { name: 'author', content: t('head.author') },
        { key: 'og:title', property: 'og:title', content: pageTitle },
        { key: 'og:description', property: 'og:description', content: pageDescription },
        { key: 'og:image', property: 'og:image', content: t('head.og.image') },
        { key: 'og:url', property: 'og:url', content: `${baseUrl}${canonicalPath}` },
        { key: 'og:type', property: 'og:type', content: 'website' },
        { key: 'og:site_name', property: 'og:site_name', content: t('head.og.site_name') },
        { key: 'og:locale', property: 'og:locale', content: localizedOgLocale(locale.value) },
        { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { key: 'twitter:title', name: 'twitter:title', content: pageTitle },
        { key: 'twitter:description', name: 'twitter:description', content: pageDescription },
        { key: 'twitter:image', name: 'twitter:image', content: t('head.twitter.image') },
        { key: 'twitter:site', name: 'twitter:site', content: t('head.twitter.site') },
        { key: 'twitter:creator', name: 'twitter:creator', content: t('head.twitter.creator') },
        { name: 'google-adsense-account', content: t('head.google_adsense_account') }
      ],
      link: [{ rel: 'icon', href: '/yumnia-Favicon.ico' }]
    }

    if (pageKeywords) {
      defaultHead.meta.push({ key: 'keywords', name: 'keywords', content: pageKeywords })
    }

    const robotsContent = resolveRouteRobots(route)
    if (robotsContent) {
      defaultHead.meta.push({ key: 'robots', name: 'robots', content: robotsContent })

      const defaultParams = new URLSearchParams({ ...route.query })
      defaultParams.delete('lang')
      const canonicalHref = `${baseUrl}${canonicalPath}${defaultParams.toString() ? `?${defaultParams.toString()}` : ''}`
      defaultHead.link.push({
        key: 'canonical',
        rel: 'canonical',
        href: canonicalHref
      })

      return defaultHead
    }

    const originalQuery = { ...route.query }
    const alternateLinks = availableLanguages.map(lang => {
      const params = new URLSearchParams(originalQuery)
      params.set('lang', lang)

      return {
        key: `alt-${lang}`,
        rel: 'alternate',
        hreflang: lang,
        href: `${baseUrl}${canonicalPath}?${params.toString()}`
      }
    })

    const defaultParams = new URLSearchParams(originalQuery)
    defaultParams.delete('lang')

    alternateLinks.push({
      key: 'canonical',
      rel: 'canonical',
      href: `${baseUrl}${canonicalPath}${defaultParams.toString() ? `?${defaultParams.toString()}` : ''}`
    })

    alternateLinks.push({
      key: 'alt-x-default',
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${baseUrl}${canonicalPath}${defaultParams.toString() ? `?${defaultParams.toString()}` : ''}`
    })

    defaultHead.link = [...defaultHead.link, ...alternateLinks]

    return defaultHead
  }))
}
